import { MyAccountServiceModule } from 'services/data/myAccount.service';
import { StoreInformationServiceModule } from 'services/data/storeInformation.service';
import { AuthTokenServiceModule } from 'services/auth/authToken.service';
import { AppSettingsModule } from 'services/settings.provider';
import { GreetingServiceModule } from 'services/data/greeting.service';
import { HubspotServiceModule } from 'services/data/hubspot.service';
import { SubscriptionServiceModule } from 'services/access/subscription/subscription.service';
import { LaunchDarklyServiceModule } from 'services/access/launchDarkly/launchDarkly.service';
import { ProductServiceModule } from 'services/data/products.service';
import { CategoryServiceModule } from 'services/data/categories.service';
import { PagesServiceModule } from 'services/data/pages.service';
import { PostageServiceModule } from 'services/data/postage.service';
import { FeaturesServiceModule } from 'services/access/features/features.service';
import { SegmentServiceModule } from 'services/tracking/segment/segment.service';
import { OrderServiceModule } from 'services/data/orders.service';
import { TrialServiceModule, NOT_TRIAL } from 'components/trial/trial.service';
import { DiscountsServiceModule } from 'services/data/discounts.service';
import { PhoneFilterModule } from 'filters/phone.filter';
import { VerifyEmailReminderServiceModule } from 'components/verifyEmailReminder/verifyEmailReminder.service';
import { loadBaremetricsRecoverWidget } from 'app/admin/services/thirdParty/BaremetricsRecover';

export const AppServiceModule = angular
    .module('app.service', [
        MyAccountServiceModule,
        StoreInformationServiceModule,
        AuthTokenServiceModule,
        AppSettingsModule,
        GreetingServiceModule,
        SubscriptionServiceModule,
        LaunchDarklyServiceModule,
        ProductServiceModule,
        CategoryServiceModule,
        PagesServiceModule,
        PostageServiceModule,
        FeaturesServiceModule,
        OrderServiceModule,
        TrialServiceModule,
        DiscountsServiceModule,
        PhoneFilterModule,
        VerifyEmailReminderServiceModule,
        SegmentServiceModule,
        HubspotServiceModule
    ])
    .service(
        'appService',
        (
            $filter,
            $window,
            $document,
            myAccountService,
            storeInformationService,
            authTokenService,
            auth0AuthService,
            $q,
            greetingService,
            subscriptionsService,
            launchDarklyService,
            discountsService,
            productService,
            categoryService,
            pagesService,
            postageService,
            featureService,
            orderService,
            $rootScope,
            $timeout,
            trialService,
            verifyEmailReminderService,
            segmentService,
            hubspotService
        ) => {
            async function init() {
                orderService.initOrderNotify();
                $q.all([
                    myAccountService.getUser(),
                    trialService.getTrialStatus(),
                    hubspotService.loadChatWidget()
                ]).then(([user, trialStatus]) => {
                    if (!user.emailVerified && trialStatus === NOT_TRIAL) {
                        verifyEmailReminderService.initVerifyEmailReminder();
                    }
                });

                getDomainStatusAndRegister();
                initLogging();
            }

            function initLogging() {
                const getGreeting = greetingService
                    .getProfile()
                    .then(response => response.data)
                    .catch(() => {
                        return {};
                    });

                $q.all([getGreeting, getInitData()]).then(
                    ([greetingData, initData]) => {
                        let mergedData = [greetingData].concat(initData);
                        initLibraries(mergedData);
                    }
                );
            }

            function getDomainStatusAndRegister() {
                storeInformationService.getStore().then((store) => {
                    storeInformationService
                        .getCustomDomainStatus()
                        .then((res) => {
                            if (
                                res.status === 'CREATE_COMPLETE' &&
                                store.customDomain
                            ) {
                                storeInformationService.registerCustomDomain(
                                    store.customDomain
                                );
                            }
                        });
                });
            }

            function getInitData() {
                const getUser = myAccountService
                    .getUser()
                    .then((res) => {
                        res = res || {};
                        return {
                            firstName: res.firstName,
                            lastName: res.lastName,
                            userName: `${res.firstName || ''} ${
                                res.lastName || ''
                            }`.trim(),
                            userId: res.id || '',
                            userEmail: res.email || ''
                        };
                    })
                    .catch(() => {
                        return {};
                    });

                const getStore = storeInformationService
                    .getStore()
                    .then((res) => {
                        return {
                            userEmail: res.email,
                            storeName: res.name,
                            taxNexus: res.taxNexus,
                            storeUrl: res.storeUrl,
                            customDomain: res.customDomain,
                            launched: res.launched,
                            materialPlan: res.materialPlan,
                            phoneNumber: $filter('phone')(res.phoneNumber),
                            paypalConnected: res.paypal
                                ? res.paypal.permissionsGranted === 'true'
                                : false,
                            paypalEnabled: res.paypal
                                ? res.paypal.isEnabled
                                : false
                        };
                    })
                    .catch(() => {
                        return {};
                    });

                const processSubscription = (subscription) => {
                    if (subscription.status === 'unpaid') { loadBaremetricsRecoverWidget(subscription.customer); }
                    return subscription;
                };

                const getSubscription = subscriptionsService
                    .getSubscription()
                    .then(res => processSubscription(res))
                    .catch(() => {
                        return {};
                    });

                const getDiscounts = Promise.all([
                    discountsService.getDiscounts(1, 1),
                    discountsService.getDiscounts(1, 1, { isActive: true })
                ])
                    .then(([allDiscounts, activeDiscounts]) => {
                        return {
                            activeDiscountCodesCount: activeDiscounts.total,
                            totalDiscountCodesCount: allDiscounts.total
                        };
                    })
                    .catch(() => {
                        return {};
                    });

                const getProductMetrics = productService
                    .getMetrics()
                    .then((resp) => {
                        return {
                            totalProductCount: resp.totalProductCount,
                            trackedSKUCount: resp.trackedSKUCount,
                            outOfStockSKUCount: resp.outOfStockSKUCount
                        };
                    })
                    .catch(() => {
                        return {};
                    });

                const getCategories = categoryService
                    .getCategories(true)
                    .then((resp) => {
                        let totalCategories =
                            categoryService.flattenCategories(resp).length;
                        let parentCategories = resp.length;
                        return {
                            categoryCount: totalCategories,
                            parentCount: parentCategories,
                            subCount: totalCategories - parentCategories
                        };
                    })
                    .catch(() => {
                        return {};
                    });

                const getPagesMetrics = pagesService
                    .getPageCount()
                    .then((resp) => {
                        return {
                            pagesCount: resp
                        };
                    })
                    .catch(() => {
                        return {};
                    });

                const getShippoDefaults = postageService
                    .getDefaults()
                    .then((resp) => {
                        return {
                            hasConfiguredDefaults:
                                postageService.hasConfiguredDefaults(resp)
                        };
                    })
                    .catch(() => {
                        return {};
                    });

                const getFeatures = featureService
                    .getFeatures()
                    .then((resp) => {
                        return {
                            shippingEnabled:
                                angular.isDefined(resp.shipping) &&
                                resp.shipping.enabled,
                            discountEnabled:
                                angular.isDefined(resp.discount) &&
                                resp.discount.enabled,
                            pinterestEnabled:
                                angular.isDefined(resp.pinterest) &&
                                resp.pinterest.enabled
                        };
                    })
                    .catch(() => {
                        return {};
                    });

                return $q.all([
                    getUser,
                    getStore,
                    getSubscription,
                    getDiscounts,
                    getProductMetrics,
                    getCategories,
                    getPagesMetrics,
                    getShippoDefaults,
                    getFeatures
                ]);
            }

            const initializeSegmentService = ({
                user,
                storeInfo,
                tenantId
            }) => {
                if ($window.analytics) {
                    segmentService.trackIdentity(tenantId, {
                        email: user.userEmail,
                        name: user.userName,
                        store_name: storeInfo.storeName,
                        store_url: storeInfo.storeUrl,
                        custom_domain: storeInfo.customDomain,
                        tenant_id: tenantId,
                        user_id: user.userId
                    });
                }
            };

            const updateLaunchDarklyUser = ({
                user,
                storeInfo,
                subscription,
                tenantId
            }) => {
                return launchDarklyService.updateUser({
                    key: tenantId,
                    firstName: user.userName,
                    email: user.userEmail,
                    custom: {
                        planType: subscription.plan.name,
                        storeName: storeInfo.storeName,
                        storeUrl: storeInfo.storeUrl
                    }
                });
            };

            const initializeLiveAgentChat = ({ user, subscription }) => {
                if ($window.embedded_svc) {
                    $window.embedded_svc.settings.prepopulatedPrechatFields = {
                        FirstName: user.firstName,
                        LastName: user.lastName,
                        Email: user.userEmail,
                        Subject: ''
                    };
                    $window.embedded_svc.settings.extraPrechatFormDetails = [
                        {
                            label: 'Email',
                            value: user.userEmail,
                            displayToAgent: false
                        },
                        {
                            label: 'Active Plan',
                            value: subscription.plan.name,
                            transcriptFields: ['Active_Plan__c'],
                            displayToAgent: false
                        },
                        {
                            label: 'Customer Platform',
                            value: 'V2',
                            transcriptFields: ['Customer_Platform__c'],
                            displayToAgent: false
                        },
                        {
                            label: 'Prime_Account_Checkbox_Test__c',
                            value: subscription.plan.name.toLowerCase().indexOf('prime') > -1,
                            transcriptFields: [
                                'Prime_Account_Checkbox_Test__c'
                            ],
                            displayToAgent: false
                        },
                        {
                            label: 'OrderDetails Name',
                            value: subscription.id,
                            displayToAgent: false
                        }
                    ];
                    $window.embedded_svc.settings.extraPrechatInfo = [
                        {
                            entityName: 'OrderDetails__c',
                            linkToEntityName: 'Case',
                            linkToEntityField: 'Order_Detail_ID__c',
                            showOnCreate: true,
                            saveToTranscript: 'Order_Detail_ID__c',
                            entityFieldMaps: [
                                {
                                    isExactMatch: true,
                                    fieldName: 'Name',
                                    doCreate: false,
                                    doFind: true,
                                    label: 'OrderDetails Name'
                                }
                            ]
                        },
                        {
                            entityName: 'Contact',
                            showOnCreate: true,
                            linkToEntityName: 'Case',
                            linkToEntityField: 'ContactId',
                            saveToTranscript: 'Contact',
                            entityFieldMaps: [
                                {
                                    isExactMatch: false,
                                    fieldName: 'FirstName',
                                    doCreate: false,
                                    doFind: false,
                                    label: 'First Name'
                                },
                                {
                                    isExactMatch: false,
                                    fieldName: 'LastName',
                                    doCreate: false,
                                    doFind: false,
                                    label: 'Last Name'
                                },
                                {
                                    isExactMatch: true,
                                    fieldName: 'Email',
                                    doCreate: true,
                                    doFind: true,
                                    label: 'Email'
                                }
                            ]
                        }
                    ];
                }
            };

            const trackAppLoadedEvent = (
                { user, subscription, storeInfo, tenantId },
                isTrial
            ) => {
                if (segmentService) {
                    segmentService.trackGtmEvent(
                        { analyticsClickEvent: 'initialAppLoaded' },
                        {
                            event: 'initialAppLoaded',
                            tenantID: tenantId,
                            userId: user.userId,
                            planType: subscription.plan.name,
                            isLaunched: storeInfo.launched.toString(),
                            isTrial
                        }
                    );
                }
            };

            const sendProfileToGoogleAnalytics = ({ greeting }) => {
                $window.dataLayer = $window.dataLayer || [];
                $window.dataLayer.push({
                    event: 'profile',
                    industry: greeting.industry,
                    path: greeting.path,
                    tenant: greeting.tenant,
                    createdOn: greeting.createdOn,
                    updatedOn: greeting.updatedOn
                });
            };

            function initLibraries([
                greetingResponse,
                userResponse,
                storeResponse,
                getSubscriptionResponse,
                discountsResponse,
                productsResponse,
                categoriesResponse,
                pagesResponse,
                shippoDefaultsResponse,
                featuresResponse
            ]) {
                return authTokenService.getTenantId().then((tenantId) => {
                    const initializationPayload = {
                        user: userResponse,
                        storeInfo: storeResponse,
                        subscription: getSubscriptionResponse,
                        greeting: greetingResponse,
                        discounts: discountsResponse,
                        products: productsResponse,
                        categories: categoriesResponse,
                        pages: pagesResponse,
                        features: featuresResponse,
                        shippoDefaults: shippoDefaultsResponse,
                        tenantId
                    };

                    initializeSegmentService(initializationPayload);

                    updateLaunchDarklyUser(initializationPayload).then(() => {
                        initializeLiveAgentChat(
                            initializationPayload
                        );
                        const helpButtons = $document.find(
                            '.embeddedServiceHelpButton'
                        );
                        if (helpButtons && helpButtons.length > 0) {
                            helpButtons[0].style.visibility =
                                'visible';
                        }
                    });

                    trialService
                        .isTrialPlan(getSubscriptionResponse)
                        .then((isTrial) => {
                            trackAppLoadedEvent(initializationPayload, isTrial);
                        });

                    sendProfileToGoogleAnalytics(initializationPayload);
                });
            }

            return {
                init,
                initLogging
            };
        }
    ).name;



// WEBPACK FOOTER //
// ./src/app/app.service.js